import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Building2, Users, FileText, Brain, LogOut, Download, 
  ChevronDown, ChevronUp, Loader2, School, Ticket 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CSRDashboard = ({ csrPartner, setCSRPartner }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [psychometricResults, setPsychometricResults] = useState([]);
  const [counselingReports, setCounselingReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReport, setExpandedReport] = useState(null);

  const token = localStorage.getItem('csr_token');

  useEffect(() => {
    if (!token) {
      navigate('/csr/login');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, studentsRes, psychRes, reportsRes] = await Promise.all([
        axios.get(`${API}/csr/dashboard-stats`, { headers }),
        axios.get(`${API}/csr/students`, { headers }),
        axios.get(`${API}/csr/psychometric-results`, { headers }),
        axios.get(`${API}/csr/counseling-reports`, { headers })
      ]);

      setStats(statsRes.data);
      setStudents(studentsRes.data.students || []);
      setPsychometricResults(psychRes.data.results || []);
      setCounselingReports(reportsRes.data.reports || []);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      } else {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('csr_token');
    localStorage.removeItem('csr_partner');
    setCSRPartner(null);
    navigate('/csr/login');
  };

  const downloadStudentsCSV = () => {
    if (students.length === 0) {
      toast.error('No students to download');
      return;
    }

    const headers = ['Name', 'Email', 'Phone', 'Grade', 'School', 'Enrollment Date'];
    const rows = students.map(s => [
      s.name,
      s.email,
      s.phone || '',
      s.grade,
      s.school || '',
      s.created_at ? new Date(s.created_at).toLocaleDateString() : ''
    ]);

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `students_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Students list downloaded');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <Building2 className="w-4 h-4" /> },
    { id: 'students', label: 'Students', icon: <Users className="w-4 h-4" /> },
    { id: 'psychometric', label: 'Psychometric Tests', icon: <FileText className="w-4 h-4" /> },
    { id: 'counseling', label: 'Career Mapping', icon: <Brain className="w-4 h-4" /> },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-slate-900">CSR Partner Portal</h1>
              <p className="text-sm text-slate-500">{csrPartner?.organization || 'Partner Dashboard'}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-600 text-sm hidden md:block">
              Welcome, {csrPartner?.name}
            </span>
            <Button
              data-testid="csr-logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="text-slate-600 border-slate-200"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              data-testid={`csr-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              className={`rounded-full ${
                activeTab === tab.id
                  ? 'bg-teal-500 hover:bg-teal-600 text-white'
                  : 'text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {tab.icon}
              <span className="ml-2">{tab.label}</span>
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-gradient-to-br from-teal-500 to-emerald-500 text-white rounded-2xl border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-teal-100 text-sm">Total Students</p>
                      <p className="text-4xl font-bold mt-1">{stats?.total_students || 0}</p>
                    </div>
                    <Users className="w-12 h-12 text-teal-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-500 to-indigo-500 text-white rounded-2xl border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Psychometric Tests</p>
                      <p className="text-4xl font-bold mt-1">{stats?.psychometric_tests || 0}</p>
                    </div>
                    <FileText className="w-12 h-12 text-blue-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-violet-500 text-white rounded-2xl border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Career Reports</p>
                      <p className="text-4xl font-bold mt-1">{stats?.career_mapping_reports || 0}</p>
                    </div>
                    <Brain className="w-12 h-12 text-purple-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Assigned Access */}
            <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Your Access</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {stats?.assigned_schools?.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500 mb-2 flex items-center gap-2">
                      <School className="w-4 h-4" /> Assigned Schools
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {stats.assigned_schools.map((school) => (
                        <span key={school} className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
                          {school}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {stats?.assigned_coupons?.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500 mb-2 flex items-center gap-2">
                      <Ticket className="w-4 h-4" /> Assigned Coupon Codes
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {stats.assigned_coupons.map((coupon) => (
                        <span key={coupon} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-mono">
                          {coupon}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {!stats?.assigned_schools?.length && !stats?.assigned_coupons?.length && (
                  <p className="text-slate-500">No schools or coupons assigned yet. Contact admin.</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-xl">
                Enrolled Students ({students.length})
              </CardTitle>
              <Button
                data-testid="download-students-csv"
                onClick={downloadStudentsCSV}
                variant="outline"
                className="text-teal-600 border-teal-200 hover:bg-teal-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            </CardHeader>
            <CardContent>
              {students.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No students enrolled yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Name</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Phone</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Grade</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">School</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Enrolled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => (
                        <tr key={student.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 font-medium text-slate-900">{student.name}</td>
                          <td className="py-3 px-4 text-slate-600">{student.email}</td>
                          <td className="py-3 px-4 text-slate-600">{student.phone || '-'}</td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-sm">
                              Grade {student.grade}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-slate-600">{student.school || '-'}</td>
                          <td className="py-3 px-4 text-slate-500 text-sm">
                            {student.created_at ? new Date(student.created_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Psychometric Tests Tab */}
        {activeTab === 'psychometric' && (
          <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-xl">
                Psychometric Test Results ({psychometricResults.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {psychometricResults.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No test results yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Student</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Grade</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Score</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Categories</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {psychometricResults.map((result) => (
                        <tr key={result.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-slate-900">{result.user_name}</p>
                              <p className="text-sm text-slate-500">{result.user_email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-sm">
                              Grade {result.grade}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              result.percentage >= 70 ? 'bg-green-100 text-green-700' :
                              result.percentage >= 50 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {result.percentage?.toFixed(1)}%
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1">
                              {result.category_scores && Object.entries(result.category_scores).map(([cat, scores]) => (
                                <span key={cat} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                                  {cat}: {((scores.score / scores.max_score) * 100).toFixed(0)}%
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-sm">
                            {result.completed_at ? new Date(result.completed_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Career Mapping Tab */}
        {activeTab === 'counseling' && (
          <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-xl">
                Career Mapping Reports ({counselingReports.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {counselingReports.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Brain className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No career mapping reports yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {counselingReports.map((report) => (
                    <div
                      key={report.report_id}
                      className="border border-slate-200 rounded-xl overflow-hidden"
                    >
                      <div
                        className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                        onClick={() => setExpandedReport(expandedReport === report.report_id ? null : report.report_id)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h4 className="font-semibold text-slate-900">{report.user_name}</h4>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              report.track === 'A' 
                                ? 'bg-indigo-100 text-indigo-700' 
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              Track {report.track}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                            <span><strong>Recommendation:</strong> {report.recommendation}</span>
                            <span><strong>Cognitive:</strong> {report.cognitive_score}%</span>
                            <span><strong>Date:</strong> {new Date(report.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        {expandedReport === report.report_id ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                      
                      {expandedReport === report.report_id && (
                        <div className="p-4 border-t border-slate-200 bg-slate-50">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            {report.scores && Object.entries(report.scores).map(([stream, score]) => (
                              <div key={stream} className="bg-white p-3 rounded-lg border border-slate-200">
                                <p className="text-sm text-slate-500">{stream}</p>
                                <p className="text-xl font-bold text-slate-900">{score}</p>
                              </div>
                            ))}
                          </div>
                          {report.gaps && report.gaps.length > 0 && (
                            <div>
                              <p className="text-sm text-slate-500 mb-2">Identified Gaps:</p>
                              <div className="flex flex-wrap gap-2">
                                {report.gaps.map((gap) => (
                                  <span key={gap} className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-sm">
                                    {gap}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CSRDashboard;
